// Force rebuild - v2
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { api } from '../apiConfig';
import endpoints from '../apiConfig';
import '../components/AdminDashboard.css';
import '../styles/admin-tailwind.css';

// Sync Artwork Button Component
const SyncArtworkButton = () => {
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [syncError, setSyncError] = useState(null);

  const getApiConfig = async () => {
    if (import.meta.env.MODE === 'development') {
      console.log('🔧 Development mode: making API calls without tokens');
      return {}; // No headers in development
    } else {
      const token = await auth.currentUser.getIdToken();
      return { headers: { Authorization: `Bearer ${token}` } };
    }
  };

  const syncArtworkMetafields = async () => {
    console.log('🔄 SYNC BUTTON CLICKED! Starting artwork sync...');
    setSyncLoading(true);
    setSyncError(null);
    setSyncResults(null);

    try {
      console.log('📡 Getting API config...');
      const config = await getApiConfig();
      console.log('📡 Making API call to /admin/sync-artwork-metafields with config:', config);
      const response = await api.post('/admin/sync-artwork-metafields', {}, config);
      console.log('✅ Sync API response:', response.data);
      setSyncResults(response.data);
    } catch (error) {
      console.error('❌ Error syncing artworks:', error);
      console.error('❌ Error details:', error.response?.data);
      setSyncError(error.response?.data?.error || 'Failed to sync artworks');
    } finally {
      setSyncLoading(false);
      console.log('🏁 Sync process completed');
    }
  };

  return (
    <div>
      <button
        onClick={syncArtworkMetafields}
        disabled={syncLoading}
        className={`px-4 py-2 text-sm font-medium rounded-md ${
          syncLoading
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-700'
        }`}
      >
        {syncLoading ? 'Syncing...' : 'Sync All Existing Artworks'}
      </button>

      {/* Sync Results */}
      {syncResults && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <h4 className="text-sm font-medium text-green-800 mb-2">✅ Sync Complete</h4>
          <p className="text-sm text-green-700 mb-3">
            Processed {syncResults.total_artworks} artworks
          </p>
          
          {syncResults.results && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {syncResults.results.map((result, index) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <span className="truncate flex-1 mr-2">{result.title}</span>
                  <span className={`px-2 py-1 rounded ${
                    result.status === 'synced' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {result.status === 'synced' ? `✅ ${result.metafields_updated} fields` : '❌ error'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sync Error */}
      {syncError && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <h4 className="text-sm font-medium text-red-800">❌ Sync Failed</h4>
          <p className="text-sm text-red-700 mt-1">{syncError}</p>
        </div>
      )}
    </div>
  );
};

// System Setup Tab Component
const SystemSetupTab = () => {
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupResults, setSetupResults] = useState(null);
  const [setupError, setSetupError] = useState(null);
  const [artistEmail, setArtistEmail] = useState('');
  const [completionLoading, setCompletionLoading] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [completionError, setCompletionError] = useState(null);
  const [priceSyncLoading, setPriceSyncLoading] = useState(false);
  const [priceSyncData, setPriceSyncData] = useState(null);
  const [priceSyncError, setPriceSyncError] = useState(null);
  const [priceSyncAllLoading, setPriceSyncAllLoading] = useState(false);

  // Helper function to get API config with or without auth tokens
  const getApiConfig = async () => {
    if (import.meta.env.MODE === 'development') {
      console.log('🔧 Development mode: making API calls without tokens');
      return {}; // No headers in development
    } else {
      const token = await auth.currentUser.getIdToken();
      return { headers: { Authorization: `Bearer ${token}` } };
    }
  };

  const setupShopifyMetafields = async () => {
    setSetupLoading(true);
    setSetupError(null);
    setSetupResults(null);

    try {
      const config = await getApiConfig();
      const response = await api.post('/admin/setup-metafields', {}, config);
      setSetupResults(response.data);
    } catch (error) {
      console.error('Error setting up metafields:', error);
      setSetupError(error.response?.data?.error || 'Failed to setup metafields');
    } finally {
      setSetupLoading(false);
    }
  };

  const checkArtistCompletion = async () => {
    const email = artistEmail.trim();
    if (!email) {
      setCompletionError('Please enter an artist email');
      return;
    }

    setCompletionLoading(true);
    setCompletionError(null);
    setCompletionData(null);

    try {
      const config = await getApiConfig();
      const response = await api.get(`/admin/artist-completion?email=${encodeURIComponent(email)}`, config);
      setCompletionData(response.data);
    } catch (error) {
      console.error('Error checking artist completion:', error);
      setCompletionError(error.response?.data?.error || 'Failed to check artist completion');
    } finally {
      setCompletionLoading(false);
    }
  };

  const resyncArtistCompletion = async () => {
    const email = artistEmail.trim();
    if (!email) {
      setCompletionError('Please enter an artist email');
      return;
    }

    setCompletionLoading(true);
    setCompletionError(null);

    try {
      const config = await getApiConfig();
      const response = await api.post('/admin/artist-completion/resync', { email }, config);
      setCompletionData(response.data);
    } catch (error) {
      console.error('Error resyncing artist completion:', error);
      setCompletionError(error.response?.data?.error || 'Failed to resync artist completion');
    } finally {
      setCompletionLoading(false);
    }
  };

  const resyncArtistPrices = async () => {
    const email = artistEmail.trim();
    if (!email) {
      setPriceSyncError('Please enter an artist email');
      return;
    }

    setPriceSyncLoading(true);
    setPriceSyncError(null);
    setPriceSyncData(null);

    try {
      const config = await getApiConfig();
      const response = await api.post('/admin/artist-prices/resync', { email }, config);
      setPriceSyncData(response.data);
    } catch (error) {
      console.error('Error resyncing artist prices:', error);
      setPriceSyncError(error.response?.data?.error || 'Failed to resync artist prices');
    } finally {
      setPriceSyncLoading(false);
    }
  };

  const resyncAllArtistPrices = async () => {
    setPriceSyncAllLoading(true);
    setPriceSyncError(null);
    setPriceSyncData(null);

    try {
      const config = await getApiConfig();
      let offset = 0;
      // Smaller batches reduce risk of platform request timeouts.
      const limit = 5;
      let hasMore = true;
      let total = 0;
      let updated = 0;
      let failed = 0;
      let processed = 0;
      const allResults = [];

      // Process in batches to avoid request timeouts on large datasets.
      while (hasMore) {
        let data = null;
        let attempt = 0;
        const maxAttempts = 3;

        while (!data && attempt < maxAttempts) {
          try {
            const response = await api.post('/admin/artist-prices/resync-all', { offset, limit }, config);
            data = response.data || {};
          } catch (batchError) {
            attempt += 1;
            if (attempt >= maxAttempts) {
              throw batchError;
            }
            // Short incremental backoff for transient 5xx/network errors.
            await new Promise(resolve => setTimeout(resolve, 500 * attempt));
          }
        }

        total = data.total || total;
        processed += data.processed || 0;
        updated += data.updated || 0;
        failed += data.failed || 0;
        if (Array.isArray(data.results)) {
          allResults.push(...data.results);
        }

        hasMore = !!data.hasMore;
        offset = data.nextOffset || offset + limit;
      }

      setPriceSyncData({
        success: failed === 0,
        total,
        processed,
        updated,
        failed,
        results: allResults
      });
    } catch (error) {
      console.error('Error resyncing all artist prices:', error);
      const details = error.response?.data?.details;
      setPriceSyncError(details ? `Failed to resync all artist prices: ${details}` : 'Failed to resync all artist prices');
    } finally {
      setPriceSyncAllLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">System Setup</h2>
      
      <div className="space-y-6">
        {/* Shopify Metafields Setup */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Shopify Metafield Definitions</h3>
              <p className="text-sm text-gray-600 mb-4">
                Create metafield definitions in Shopify to enable artwork fields (medium, dimensions, etc.) to display in themes.
                This only needs to be done once.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <p>• <strong>Custom fields:</strong> dimensions, year_created, edition_type, edition_size, edition_number, style, tags</p>
                <p>• <strong>Standard fields:</strong> medium</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <button
              onClick={setupShopifyMetafields}
              disabled={setupLoading}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                setupLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {setupLoading ? 'Setting up...' : 'Setup Metafield Definitions'}
            </button>
          </div>

          {/* Results */}
          {setupResults && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <h4 className="text-sm font-medium text-green-800 mb-2">✅ Setup Complete</h4>
              <div className="text-sm text-green-700 space-y-1">
                {setupResults.results.map((result, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span>{result.key}</span>
                    <span className={`px-2 py-1 text-xs rounded ${
                      result.status === 'created' ? 'bg-green-100 text-green-800' :
                      result.status === 'exists' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {result.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {setupError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <h4 className="text-sm font-medium text-red-800">❌ Setup Failed</h4>
              <p className="text-sm text-red-700 mt-1">{setupError}</p>
            </div>
          )}
        </div>

        {/* Sync Existing Artworks */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Sync Existing Artworks</h3>
              <p className="text-sm text-gray-600 mb-4">
                Update all existing Shopify products with the correct metafield data from the database.
                This will fix artist names and add missing artwork details.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <p>• <strong>Updates:</strong> Artist name (vendor), medium, dimensions, year created, edition info</p>
                <p>• <strong>Adds:</strong> AI analysis data (style, tags) as metafields</p>
                <p>• <strong>Applies to:</strong> All published artworks with Shopify product IDs</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
                          <SyncArtworkButton />
              
              <button
                onClick={async () => {
                  try {
                    // Use the same logic as apiConfig.js to determine the correct API URL
                    const getApiUrl = () => {
                      if (import.meta.env.VITE_API_BASE_URL) {
                        return import.meta.env.VITE_API_BASE_URL;
                      }
                      if (import.meta.env.PROD || window.location.hostname === 'app.artemerge.org') {
                        return 'https://art-emerge-4744deb5e11a.herokuapp.com/api';
                      }
                      return 'http://localhost:5051/api';
                    };
                    
                    const API_BASE_URL = getApiUrl();
                    console.log('🔗 Making request to:', `${API_BASE_URL}/admin/check-medium-metaobjects`);
                    
                    const response = await fetch(`${API_BASE_URL}/admin/check-medium-metaobjects`, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                      },
                    });
                    
                    console.log('📡 Response status:', response.status);
                    console.log('📡 Response headers:', response.headers.get('content-type'));
                    
                    const text = await response.text();
                    console.log('🔍 RAW RESPONSE:', text.substring(0, 500) + '...');
                    
                    if (text.startsWith('<!doctype') || text.startsWith('<html')) {
                      console.log('❌ Got HTML instead of JSON - probably a routing/auth error');
                      console.log('🔍 Full HTML response:', text);
                    } else {
                      try {
                        const data = JSON.parse(text);
                        console.log('🔍 METAOBJECT DEBUG:', JSON.stringify(data, null, 2));
                      } catch (parseError) {
                        console.log('❌ Failed to parse as JSON:', parseError);
                        console.log('🔍 Raw text:', text);
                      }
                    }
                  } catch (error) {
                    console.error('❌ Error checking metaobjects:', error);
                  }
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                🔍 Debug Metaobjects
              </button>
          </div>
        </div>

        {/* Artist Completion Tools */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Artist Completion Tools</h3>
              <p className="text-sm text-gray-600 mb-4">
                Check or resync an artist&apos;s completion state (profile, artwork, Stripe, and published status) by email.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={artistEmail}
              onChange={(e) => setArtistEmail(e.target.value)}
              placeholder="artist@email.com"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            />
            <button
              onClick={checkArtistCompletion}
              disabled={completionLoading}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                completionLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {completionLoading ? 'Working...' : 'Check Completion'}
            </button>
            <button
              onClick={resyncArtistCompletion}
              disabled={completionLoading}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                completionLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {completionLoading ? 'Working...' : 'Resync Completion'}
            </button>
          </div>

          {completionData && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <h4 className="text-sm font-medium text-green-800 mb-2">✅ Completion Data</h4>
              <div className="text-xs text-green-900 bg-white border border-green-100 rounded p-3 overflow-x-auto">
                <pre>{JSON.stringify(completionData, null, 2)}</pre>
              </div>
            </div>
          )}

          {completionError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <h4 className="text-sm font-medium text-red-800">❌ Request Failed</h4>
              <p className="text-sm text-red-700 mt-1">{completionError}</p>
            </div>
          )}
        </div>

        {/* Artist Price Sync */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Artist Price Sync</h3>
              <p className="text-sm text-gray-600 mb-4">
                One-time backfill tool to resync Shopify storefront prices from dashboard DB prices for all published artworks.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={artistEmail}
              onChange={(e) => setArtistEmail(e.target.value)}
              placeholder="artist@email.com"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            />
            <button
              onClick={resyncArtistPrices}
              disabled={priceSyncLoading}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                priceSyncLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
            >
              {priceSyncLoading ? 'Resyncing Prices...' : 'Resync Storefront Prices'}
            </button>
            <button
              onClick={resyncAllArtistPrices}
              disabled={priceSyncAllLoading}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                priceSyncAllLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {priceSyncAllLoading ? 'Resyncing All...' : 'Resync All Artists Prices'}
            </button>
          </div>

          {priceSyncData && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <h4 className="text-sm font-medium text-green-800 mb-2">✅ Price Sync Result</h4>
              <p className="text-sm text-green-700 mb-3">
                Processed {priceSyncData.processed || 0}, updated {priceSyncData.updated || 0}, failed {priceSyncData.failed || 0}
              </p>
              <div className="text-xs text-green-900 bg-white border border-green-100 rounded p-3 overflow-x-auto">
                <pre>{JSON.stringify(priceSyncData, null, 2)}</pre>
              </div>
            </div>
          )}

          {priceSyncError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <h4 className="text-sm font-medium text-red-800">❌ Price Sync Failed</h4>
              <p className="text-sm text-red-700 mt-1">{priceSyncError}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PayoutsTab = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const getApiConfig = async () => {
    if (import.meta.env.MODE === 'development') {
      return {};
    }
    const token = await auth.currentUser.getIdToken();
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const formatMoney = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fetchPayouts = async () => {
    setLoading(true);
    setError(null);
    try {
      const config = await getApiConfig();
      const response = await api.get('/admin/payouts/pending', config);
      setPayouts(response.data.payouts || []);
    } catch (fetchError) {
      console.error('Error fetching payouts:', fetchError);
      setError(fetchError.response?.data?.error || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, []);

  const runAction = async (id, action) => {
    setActionLoading(`${action}:${id}`);
    setError(null);
    try {
      const config = await getApiConfig();
      await api.post(`/admin/payouts/${id}/${action}`, {}, config);
      await fetchPayouts();
    } catch (actionError) {
      console.error(`Error running payout action ${action}:`, actionError);
      setError(actionError.response?.data?.error || `Failed to ${action.replace('-', ' ')}`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Artist Payout Review</h2>
            <p className="text-sm text-gray-600 mt-1">
              Release Stripe Connect payouts only after Shopify settlement is confirmed and the sale has been reviewed.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchPayouts}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Artwork</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Artist</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Order</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Buyer Paid</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Artist Payout</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {payouts.map((payout) => {
                const settled = payout.shopify_settlement_status === 'settled';
                const paidOut = payout.payout_status === 'paid_out';
                const canRelease = settled && !paidOut && payout.artist_stripe_account_id;
                return (
                  <tr key={payout.id}>
                    <td className="px-3 py-3 align-top">
                      <div className="font-medium text-gray-900">{payout.artwork_title || payout.artwork_id}</div>
                      <div className="text-xs text-gray-500">{payout.shopify_product_id}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div>{payout.artist_name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{payout.artist_email}</div>
                      {!payout.artist_stripe_account_id && (
                        <div className="text-xs text-red-600">No Stripe account</div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div>{payout.shopify_order_name || payout.shopify_order_id}</div>
                      <div className="text-xs text-gray-500">{payout.shopify_financial_status}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div>{formatMoney(payout.buyer_paid_amount)}</div>
                      <div className="text-xs text-gray-500">Base {formatMoney(payout.artist_base_price)}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="font-medium">{formatMoney(payout.artist_payout_amount)}</div>
                      <div className="text-xs text-gray-500">Seller fee {formatMoney(payout.seller_fee)}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="font-medium">{payout.payout_status}</div>
                      <div className="text-xs text-gray-500">Settlement: {payout.shopify_settlement_status}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-col gap-2">
                        {!settled && (
                          <button
                            type="button"
                            onClick={() => runAction(payout.id, 'mark-settled')}
                            disabled={actionLoading === `mark-settled:${payout.id}`}
                            className="px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                          >
                            Mark Settled
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => runAction(payout.id, 'release')}
                          disabled={!canRelease || actionLoading === `release:${payout.id}`}
                          className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          Release Payout
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && payouts.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-3 py-8 text-center text-gray-500">
                    No pending payouts.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    totalArtworks: 0,
    publishedArtworks: 0,
    roleDistribution: {
      artist: 0,
      collector: 0,
      gallery: 0,
      curator: 0,
      admin: 0
    }
  });
  const [users, setUsers] = useState([]);
  const [artistsActivity, setArtistsActivity] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [artworksPagination, setArtworksPagination] = useState({});
  
  // Modal states
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [artistDrawerLoading, setArtistDrawerLoading] = useState(false);
  const [userDrawerLoading, setUserDrawerLoading] = useState(false);
  
  // Filter states
  const [artworkFilter, setArtworkFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [artistSearch, setArtistSearch] = useState('');
  const [artistSortKey, setArtistSortKey] = useState(null); // 'totalArtworks' | 'publishedArtworks' | 'liveOnShopify'
  const [artistSortDirection, setArtistSortDirection] = useState('desc'); // 'asc' | 'desc'

  const toggleArtistSort = (key) => {
    if (artistSortKey === key) {
      setArtistSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setArtistSortKey(key);
      setArtistSortDirection('desc');
    }
  };

  const getSortedArtists = (list) => {
    if (!artistSortKey) return list;
    const directionMultiplier = artistSortDirection === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const aValue = Number(a?.[artistSortKey] || 0);
      const bValue = Number(b?.[artistSortKey] || 0);
      if (aValue === bValue) return 0;
      return aValue > bValue ? directionMultiplier : -directionMultiplier;
    });
  };

  // Fetch users with optional role/search filters from the server
  const fetchUsers = async (role = 'all', search = '') => {
    try {
      const config = await getApiConfig();
      const params = new URLSearchParams();
      if (role && role !== 'all') params.append('role', role);
      if (search && search.trim()) params.append('search', search.trim());
      const url = params.toString() ? `${endpoints.admin.users}?${params}` : endpoints.admin.users;
      const res = await api.get(url, config);
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users with filters:', err);
    }
  };

  // Helper function to get API config with or without auth tokens
  const getApiConfig = async () => {
    if (import.meta.env.MODE === 'development') {
      console.log('🔧 Development mode: making API calls without tokens');
      return {}; // No headers in development
    } else {
      const token = await auth.currentUser.getIdToken();
      return { headers: { Authorization: `Bearer ${token}` } };
    }
  };

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        // In development mode, skip Firebase auth and just check profile
        if (import.meta.env.MODE === 'development') {
          console.log('🔧 Development mode: skipping Firebase auth');
          const response = await api.get('/users/profile');
          
          console.log('📝 User profile response:', response.data);
          console.log('🔐 User role:', response.data.role);

          if (response.data.role !== 'admin') {
            console.log('❌ User is not admin, redirecting to account');
            navigate('/account');
            return;
          }

          console.log('✅ Admin access confirmed, loading dashboard...');
          await fetchDashboardData();
          return;
        }

        // Production mode: use Firebase auth
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.log('❌ No currentUser, waiting for auth...');
          setTimeout(checkAdminAccess, 1000);
          return;
        }

        console.log('👤 Current user:', currentUser.email);
        const token = await currentUser.getIdToken();
        const response = await api.get('/users/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });

        console.log('📝 User profile response:', response.data);
        console.log('🔐 User role:', response.data.role);

        if (response.data.role !== 'admin') {
          console.log('❌ User is not admin, redirecting to account');
          navigate('/account');
          return;
        }

        console.log('✅ Admin access confirmed, loading dashboard...');
        await fetchDashboardData();
      } catch (error) {
        console.error('❌ Admin access check failed:', error);
        navigate('/account');
      }
    };

    // In development mode, skip Firebase auth state checking
    if (import.meta.env.MODE === 'development') {
      console.log('🔧 Development mode: skipping Firebase auth state listener');
      checkAdminAccess();
      return () => {}; // No cleanup needed in development
    }

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        console.log('🔥 Auth state changed, user available:', user.email);
        checkAdminAccess();
      } else {
        console.log('🔥 Auth state changed, no user, redirecting to account');
        navigate('/account');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const config = await getApiConfig();
      
      const [statsRes, artistsRes, recentRes] = await Promise.all([
        api.get(endpoints.admin.dashboardStats, config),
        api.get(endpoints.admin.artistsActivity, config),
        api.get(endpoints.admin.recentActivity, config)
      ]);

      // Normalize backend stats to the shape this component expects
      const statsData = statsRes.data || {};
      const normalizedStats = {
        totalUsers: statsData.users?.total || 0,
        totalArtworks: statsData.artworks?.total || 0,
        publishedArtworks: statsData.artworks?.published || 0,
        roleDistribution: statsData.users?.roleDistribution || {}
      };
      setDashboardStats(normalizedStats);
      await fetchUsers('all', '');
      setArtistsActivity(artistsRes.data || []);
      setRecentActivity(recentRes.data || []);
      
      // Fetch artworks for the new tab
      await fetchArtworks();
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchArtworks = async (status = null, page = 1) => {
    try {
      const config = await getApiConfig();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (status && status !== 'all') {
        params.append('status', status);
      }
      
      const response = await api.get(`${endpoints.admin.artworks}?${params}`, config);
      
      setArtworks(response.data.artworks || []);
      setArtworksPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error fetching artworks:', error);
    }
  };

  const fetchArtistDetails = async (artistId) => {
    try {
      setArtistDrawerLoading(true);
      setShowArtistModal(true);
      const config = await getApiConfig();
      const response = await api.get(endpoints.admin.artistDetails(artistId), config);
      if (response.data && response.data.artist) {
        setSelectedArtist(response.data);
      } else {
        console.error('Invalid artist details response structure:', response.data);
      }
    } catch (error) {
      console.error('Error fetching artist details:', error);
      // Don't show modal on error
    }
    finally {
      setArtistDrawerLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      setUserDrawerLoading(true);
      setShowUserModal(true);
      const config = await getApiConfig();
      const response = await api.get(endpoints.admin.userDetailsExpanded(userId), config);
      setSelectedUser(response.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
    finally {
      setUserDrawerLoading(false);
    }
  };

  const handleArtworkFilterChange = (newFilter) => {
    setArtworkFilter(newFilter);
    fetchArtworks(newFilter);
  };

  const StatusBadge = ({ status, isLiveOnShopify }) => {
    const getStatusColor = () => {
      if (status === 'published' && isLiveOnShopify) return 'bg-green-100 text-green-800';
      if (status === 'published') return 'bg-blue-100 text-blue-800';
      return 'bg-gray-100 text-gray-800';
    };

    const getStatusText = () => {
      if (status === 'published' && isLiveOnShopify) return 'Live';
      if (status === 'published') return 'Published';
      return 'Draft';
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
        {getStatusText()}
        {isLiveOnShopify && <span className="ml-1">🛒</span>}
      </span>
    );
  };

  const StatCard = ({ title, value, subtitle, icon, onClick }) => (
    <div 
      className={`bg-white p-6 rounded-lg shadow-sm border border-gray-200 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        {icon && <div className="text-2xl">{icon}</div>}
      </div>
    </div>
  );

  const ArtistModal = () => {
    if (!selectedArtist || !selectedArtist.artist) {
      console.log('ArtistModal: selectedArtist or selectedArtist.artist is null/undefined:', selectedArtist);
      return null;
    }

    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40" onClick={() => setShowArtistModal(false)} />
        <aside className="absolute right-0 top-0 h-full w-full sm:w-[560px] bg-white shadow-xl border-l border-gray-200 flex flex-col">
          <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{(selectedArtist && selectedArtist.artist && selectedArtist.artist.name) || 'Unknown Artist'}</h3>
                <p className="text-sm text-gray-600">{(selectedArtist && selectedArtist.artist && selectedArtist.artist.email) || 'No email'}</p>
              </div>
              <button
                onClick={() => setShowArtistModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-6 overflow-y-auto">
            {artistDrawerLoading && (
              <div className="pb-4 text-sm text-gray-500">Loading artist data…</div>
            )}
            {/* Artist Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Total Artworks"
                value={selectedArtist.stats?.total_artworks || 0}
                icon="🎨"
              />
              <StatCard
                title="Published"
                value={selectedArtist.stats?.published_artworks || 0}
                icon="✅"
              />
              <StatCard
                title="Live on Shopify"
                value={selectedArtist.stats?.live_on_shopify || 0}
                icon="🛒"
              />
              <StatCard
                title="Total Value"
                value={`$${selectedArtist.stats?.total_value?.toLocaleString() || '0'}`}
                icon="💰"
              />
            </div>

            {/* Artist's Artworks */}
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Artworks</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(selectedArtist.artworks || []).map((artwork) => (
                    <tr key={artwork.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{artwork.title}</div>
                        {artwork.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">{artwork.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${parseFloat(artwork.price || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={artwork.status} isLiveOnShopify={artwork.is_live_on_shopify} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(artwork.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </aside>
      </div>
    );
  };

  const UserModal = () => {
    if (!selectedUser) return null;

    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40" onClick={() => setShowUserModal(false)} />
        <aside className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-xl border-l border-gray-200 flex flex-col">
          <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedUser.user.name}</h3>
                <p className="text-sm text-gray-600">{selectedUser.user.email}</p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                  {selectedUser.user.role}
                </span>
              </div>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-6 overflow-y-auto">
            {userDrawerLoading && (
              <div className="pb-4 text-sm text-gray-500">Loading user data…</div>
            )}
            {/* User Details */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Profile Information</h4>
                <div className="mt-2 text-sm text-gray-600">
                  <p><strong>Location:</strong> {selectedUser.user.location || 'Not provided'}</p>
                  <p><strong>Website:</strong> {selectedUser.user.website || 'Not provided'}</p>
                  <p><strong>Joined:</strong> {new Date(selectedUser.user.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Artist-specific information */}
              {selectedUser.user.role === 'artist' && selectedUser.artwork_stats && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <StatCard
                      title="Artworks"
                      value={selectedUser.artwork_stats.total}
                      icon="🎨"
                    />
                    <StatCard
                      title="Published"
                      value={selectedUser.artwork_stats.published}
                      icon="✅"
                    />
                    <StatCard
                      title="Live"
                      value={selectedUser.artwork_stats.live_on_shopify}
                      icon="🛒"
                    />
                    <StatCard
                      title="Drafts"
                      value={selectedUser.artwork_stats.draft}
                      icon="📝"
                    />
                  </div>

                  {selectedUser.artworks && selectedUser.artworks.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Artworks</h4>
                      <div className="space-y-2">
                        {selectedUser.artworks.slice(0, 5).map((artwork) => (
                          <div key={artwork.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{artwork.title}</p>
                              <p className="text-sm text-gray-500">${parseFloat(artwork.price || 0).toLocaleString()}</p>
                            </div>
                            <StatusBadge status={artwork.status} isLiveOnShopify={artwork.is_live_on_shopify} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </aside>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600">Platform overview and user management</p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: '📊' },
              { id: 'users', name: 'Users', icon: '👥' },
              { id: 'artists', name: 'Artists', icon: '🎨' },
              { id: 'artworks', name: 'Artworks', icon: '🖼️' },
              { id: 'payouts', name: 'Payouts', icon: '💸' },
              { id: 'activity', name: 'Recent Activity', icon: '📈' },
              { id: 'system', name: 'System Setup', icon: '⚙️' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && dashboardStats && (
          <div className="space-y-8">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
              <div className="flex items-center justify-between">
            <div>
                  <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
                  <p className="text-blue-100 text-lg">Platform overview and user management</p>
                </div>
                <div className="hidden md:block">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-3xl">👑</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer group" onClick={() => setActiveTab('users')}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Total Users</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardStats.totalUsers || 0}</p>
                    <p className="text-sm text-green-600 mt-1">↗ All active users</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <span className="text-2xl">👥</span>
                  </div>
              </div>
            </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer group" onClick={() => setActiveTab('artists')}>
                <div className="flex items-center justify-between">
            <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Artists</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardStats.roleDistribution?.artist || 0}</p>
                    <p className="text-sm text-purple-600 mt-1">🎨 Content creators</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <span className="text-2xl">🎨</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer group" onClick={() => setActiveTab('artworks')}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Published</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardStats.publishedArtworks || 0}</p>
                    <p className="text-sm text-green-600 mt-1">✅ Live artworks</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <span className="text-2xl">🖼️</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer group" onClick={() => setActiveTab('artworks')}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Total Artworks</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardStats.totalArtworks || 0}</p>
                    <p className="text-sm text-blue-600 mt-1">📊 All content</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                    <span className="text-2xl">📈</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* User Roles Distribution */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">User Roles</h3>
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">📊</span>
                  </div>
                </div>
                <div className="space-y-4">
                  {Object.entries(dashboardStats.roleDistribution || {}).map(([role, count]) => {
                    const roleIcons = {
                      artist: '🎨',
                      collector: '🏛️', 
                      gallery: '🖼️',
                      curator: '👨‍🎨',
                      admin: '👑'
                    };
                    const roleColors = {
                      artist: 'bg-purple-100 text-purple-600',
                      collector: 'bg-blue-100 text-blue-600',
                      gallery: 'bg-green-100 text-green-600', 
                      curator: 'bg-orange-100 text-orange-600',
                      admin: 'bg-red-100 text-red-600'
                    };
                    return (
                      <div key={role} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${roleColors[role] || 'bg-gray-100'}`}>
                            <span className="text-lg">{roleIcons[role] || '👤'}</span>
                          </div>
                          <span className="font-medium text-gray-900 capitalize">{role}</span>
                        </div>
                        <span className="text-xl font-bold text-gray-900">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">Quick Actions</h3>
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">⚡</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    onClick={() => setActiveTab('users')} 
                    className="p-4 text-center rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                  >
                    <div className="text-2xl mb-2">👥</div>
                    <div className="text-sm font-medium text-gray-900 group-hover:text-blue-700">Manage Users</div>
                  </button>
                  <button 
                    onClick={() => setActiveTab('artworks')} 
                    className="p-4 text-center rounded-xl border-2 border-dashed border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all group"
                  >
                    <div className="text-2xl mb-2">🎨</div>
                    <div className="text-sm font-medium text-gray-900 group-hover:text-purple-700">Review Art</div>
                  </button>
                  <button 
                    onClick={() => setActiveTab('artists')} 
                    className="p-4 text-center rounded-xl border-2 border-dashed border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all group"
                  >
                    <div className="text-2xl mb-2">⭐</div>
                    <div className="text-sm font-medium text-gray-900 group-hover:text-green-700">Artist Tools</div>
                  </button>
                  <button 
                    onClick={() => setActiveTab('activity')} 
                    className="p-4 text-center rounded-xl border-2 border-dashed border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all group"
                  >
                    <div className="text-2xl mb-2">📊</div>
                    <div className="text-sm font-medium text-gray-900 group-hover:text-orange-700">Analytics</div>
                  </button>
                  <button 
                    onClick={() => setActiveTab('system')} 
                    className="p-4 text-center rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                  >
                    <div className="text-2xl mb-2">🔄</div>
                    <div className="text-sm font-medium text-gray-900 group-hover:text-blue-700">Sync Metafields</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Users ({users.filter(u => {
                  const q = userSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                }).length})</h2>
                <p className="text-sm text-gray-500">Search, filter and inspect user accounts</p>
              </div>
              <div className="flex gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search name or email"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="absolute left-2 top-2.5 text-gray-400">🔎</span>
                </div>
                <select
                  value={userFilter}
                  onChange={async (e) => {
                    const newRole = (e.target.value || '').toLowerCase();
                    setUserFilter(newRole);
                    await fetchUsers(newRole, userSearch);
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="all">All Roles</option>
                  <option value="artist">Artists</option>
                  <option value="collector">Collectors</option>
                  <option value="gallery">Galleries</option>
                  <option value="curator">Curators</option>
                  <option value="admin">Admins</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Artworks</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users
                    .filter(u => {
                      const q = userSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                    })
                    .map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <button 
                            className="text-sm font-medium text-blue-600 hover:text-blue-800"
                            onClick={() => fetchUserDetails(user.id)}
                          >
                            {user.name}
                          </button>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.role === 'artist' ? (
                          <div>
                            <div>{user.artworkCount || 0} total</div>
                            <div className="text-xs text-gray-500">
                              {user.publishedCount || 0} published
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => fetchUserDetails(user.id)}
                          className="px-3 py-1.5 rounded-md text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200 transition-colors"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'artists' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Artists</h2>
                <p className="text-sm text-gray-500">Monitor artist activity and inventory</p>
                {(() => {
                  const validArtists = (artistsActivity || []).filter(a => a && a.id);
                  const artistsWithPublishedCount = validArtists.filter(a => (a.publishedArtworks || 0) > 0).length;
                  return (
                    <p className="text-sm text-gray-500 mt-1">
                      {validArtists.length} total • {artistsWithPublishedCount} with published
                    </p>
                  );
                })()}
              </div>
              <div className="flex gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search artist name or email"
                    value={artistSearch}
                    onChange={(e) => setArtistSearch(e.target.value)}
                    className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="absolute left-2 top-2.5 text-gray-400">🔎</span>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Artist</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"
                        onClick={() => toggleArtistSort('totalArtworks')}
                      >
                        Total
                        <span className="text-[10px]">
                          {artistSortKey === 'totalArtworks' ? (artistSortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"
                        onClick={() => toggleArtistSort('publishedArtworks')}
                      >
                        Published
                        <span className="text-[10px]">
                          {artistSortKey === 'publishedArtworks' ? (artistSortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"
                        onClick={() => toggleArtistSort('liveOnShopify')}
                      >
                        Live
                        <span className="text-[10px]">
                          {artistSortKey === 'liveOnShopify' ? (artistSortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedArtists(
                    artistsActivity
                      .filter(a => a && a.id)
                      .filter(a => {
                        const q = artistSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q);
                      })
                  )
                    .map((artist) => (
                    <tr key={artist.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <button 
                            className="text-sm font-medium text-blue-600 hover:text-blue-800"
                            onClick={() => fetchArtistDetails(artist.id)}
                          >
                            {artist.name || 'Unknown Artist'}
                          </button>
                          <div className="text-sm text-gray-500">{artist.email || 'No email'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {artist.totalArtworks || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {artist.publishedArtworks || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900">{artist.liveOnShopify || 0}</span>
                          {(artist.liveOnShopify || 0) > 0 && (
                            <span className="ml-2">🛒</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {artist.lastActivity ? new Date(artist.lastActivity).toLocaleDateString() : 'No activity'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => fetchArtistDetails(artist.id)}
                          className="px-3 py-1.5 rounded-md text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200 transition-colors"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'artworks' && (
          <div>
            {/* Header with improved controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Artworks
              </h2>
                <p className="text-gray-600 mt-1">
                  {artworksPagination.total || artworks.length} total artworks
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={artworkFilter}
                onChange={(e) => handleArtworkFilterChange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20"
              >
                <option value="all">All Status</option>
                <option value="published">Published Only</option>
                <option value="draft">Drafts Only</option>
              </select>
                
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm">
                  Export Data
                </button>
              </div>
            </div>
            
            {/* Modern Card Grid Layout */}
            <div className="grid gap-6">
              {artworks.length === 0 ? (
                <div className="col-span-full bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No artworks found</h3>
                  <p className="text-gray-600 mb-6">There are currently no artworks in the system matching your criteria.</p>
                  <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm">
                    Add First Artwork
                  </button>
                </div>
              ) : 
                artworks.map((artwork) => (
                <div key={artwork.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      {/* Left Column - Main Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-4">
                          {/* Artwork Image Placeholder */}
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            {artwork.image_url ? (
                              <img 
                                src={artwork.image_url} 
                                alt={artwork.title}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                          )}
                        </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
                              {artwork.title}
                            </h3>
                            
                            {/* Artist Info */}
                            <div className="flex items-center gap-2 mb-3">
                              <button 
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                          onClick={() => fetchArtistDetails(artwork.artist_id)}
                        >
                          {artwork.artist_name}
                              </button>
                              <span className="text-gray-400">•</span>
                              <span className="text-sm text-gray-500">{artwork.artist_email}</span>
                        </div>
                            
                            {/* Description */}
                            {artwork.description && (
                              <div className="mb-4">
                                <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                                  {artwork.description}
                                </p>
                                {artwork.description.length > 120 && (
                                  <button className="text-sm text-blue-600 hover:text-blue-800 mt-1">
                                    Read more
                                  </button>
                                )}
                              </div>
                            )}
                            
                            {/* Metadata */}
                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                              <span>Created {new Date(artwork.created_at).toLocaleDateString()}</span>
                              <span>•</span>
                              <span>Updated {new Date(artwork.updated_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right Column - Status & Actions */}
                      <div className="flex flex-col items-end gap-3 ml-6">
                        {/* Price */}
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">
                        ${parseFloat(artwork.price || 0).toLocaleString()}
                          </div>
                        </div>
                        
                        {/* Status */}
                        <div className="flex items-center gap-2">
                        <StatusBadge status={artwork.status} isLiveOnShopify={artwork.is_live_on_shopify} />
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Enhanced Pagination */}
            {artworksPagination.totalPages > 1 && (
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-white rounded-xl border border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-medium text-gray-900">
                    {((artworksPagination.page - 1) * artworksPagination.limit) + 1}
                  </span> to <span className="font-medium text-gray-900">
                    {Math.min(artworksPagination.page * artworksPagination.limit, artworksPagination.total)}
                  </span> of <span className="font-medium text-gray-900">
                    {artworksPagination.total}
                  </span> results
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchArtworks(artworkFilter, artworksPagination.page - 1)}
                    disabled={artworksPagination.page <= 1}
                    className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, artworksPagination.totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => fetchArtworks(artworkFilter, pageNum)}
                          className={`w-10 h-10 text-sm font-medium rounded-lg transition-colors ${
                            pageNum === artworksPagination.page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    {artworksPagination.totalPages > 5 && (
                      <>
                        <span className="px-2 text-gray-400">...</span>
                        <button
                          onClick={() => fetchArtworks(artworkFilter, artworksPagination.totalPages)}
                          className={`w-10 h-10 text-sm font-medium rounded-lg transition-colors ${
                            artworksPagination.totalPages === artworksPagination.page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          {artworksPagination.totalPages}
                        </button>
                      </>
                    )}
                  </div>
                  
                  <button
                    onClick={() => fetchArtworks(artworkFilter, artworksPagination.page + 1)}
                    disabled={artworksPagination.page >= artworksPagination.totalPages}
                    className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 transition-colors"
                  >
                    Next
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h2>
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <div className="divide-y divide-gray-200">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="p-6">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <span className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                          {activity.type === 'user_registration' ? '👤' : 
                           activity.type === 'artwork_upload' ? '🎨' : 
                           activity.type === 'artwork_published' ? '✅' : '📝'}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {activity.description}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(activity.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'payouts' && (
          <PayoutsTab />
        )}

        {activeTab === 'system' && (
          <SystemSetupTab />
        )}
      </div>

      {/* Modals */}
      {showArtistModal && <ArtistModal />}
      {showUserModal && <UserModal />}
    </div>
  );
};

export default AdminDashboard; 